/**
 * React hook for handling unhandled promise rejections.
 * Specifically handles "Invalid data" errors that indicate expired sessions.
 *
 * This hook sets up a global error handler that catches unhandled promise rejections,
 * particularly "Invalid data" errors that occur when HTML responses are parsed as JSON
 * (common when sessions expire and users are redirected to login pages).
 *
 * @param options - Configuration options
 * @param options.toast - Toast function from `useToast()` hook
 * @param options.enabled - Whether the handler should be active (default: true)
 * @param options.redirectDelay - Delay before redirect in milliseconds (default: 2000)
 * @param options.redirectPath - Path to redirect to (default: '/')
 *
 * @example
 * ```tsx
 * import { useToast } from '@/hooks/use-toast';
 * import { useUnhandledRejectionHandler } from '@/hooks/use-unhandled-rejection-handler';
 *
 * function MyComponent() {
 *   const { toast } = useToast();
 *   useUnhandledRejectionHandler({ toast });
 *   // ... rest of component
 * }
 * ```
 *
 * @see {@link ../lib/utils/error-handling.ts} - Related error handling utilities
 * @see {@link ../lib/utils/response-utils.ts} - HTML response detection utilities
 */
import { useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { logError } from '@/lib/utils';
import { EXPIRED_SESSION_ERROR_PATTERNS } from '@/lib/utils/response-utils';
import { isThirdPartyStack } from '@/lib/sentry/third-party-domains';

export function useUnhandledRejectionHandler(options: {
  toast: (props: { title: string; description: string; variant: 'destructive' }) => void;
  enabled?: boolean;
  redirectDelay?: number;
  redirectPath?: string;
}): void {
  const { toast, enabled = true, redirectDelay = 2000, redirectPath = '/' } = options;

  const handleUnhandledRejection = useCallback(
    (event: PromiseRejectionEvent) => {
      if (!enabled) {
        return;
      }

      const reason = event.reason;
      const errorMessage =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : String(reason);

      // Check if this is a session-related error from our API
      // Be careful not to catch errors from third-party scripts (e.g., Crisp chat widget)
      const isFromThirdParty =
        reason instanceof Error && reason.stack && isThirdPartyStack(reason.stack);

      if (isFromThirdParty) {
        // Log but don't redirect for third-party script errors
        logger.debug('Ignoring unhandled rejection from third-party script', {
          errorMessage,
          stackPreview: reason instanceof Error ? reason.stack?.substring(0, 200) : undefined,
        });
        return;
      }

      // Only redirect for specific session-related error patterns
      // "Invalid data" alone is too broad - require more specific patterns
      const isSessionExpiredError = EXPIRED_SESSION_ERROR_PATTERNS.some((pattern) =>
        errorMessage.toLowerCase().includes(pattern.toLowerCase())
      );

      if (isSessionExpiredError) {
        event.preventDefault(); // Prevent default browser error handling
        logError(
          'Unhandled promise rejection: Session expired',
          {
            errorMessage,
            hint: 'This likely indicates an expired session or HTML response being parsed as JSON',
          },
          reason
        );
        toast({
          title: 'Session Expired',
          description: 'Your session has expired. Please refresh the page to log in again.',
          variant: 'destructive',
        });
        // Redirect to login after a short delay
        setTimeout(() => {
          window.location.href = redirectPath;
        }, redirectDelay);
      }
    },
    [enabled, toast, redirectDelay, redirectPath]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [handleUnhandledRejection, enabled]);
}
